import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  FeatureName,
  NumberOfShiftPerDayRecord,
  RoleAction,
  RolePermissionSubject,
  Shift,
} from '@wilson/interfaces';
import { getStartDatetime } from '@wilson/non-domain-specific/overlap-helpers';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import { ProductivityDataSourceService } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  ShiftTimelineUnassignedRegionState,
  ShiftTimelineUnassignedRegionStateFacadeService,
  TimelineDataUtilityState,
  TimelinePublishShifts,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import {
  ShiftReleaseProgressModalComponent,
  ShiftReleaseProgressModalData,
} from '@wilson/shift/core';
import { isWithinInterval } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Component({
  selector: 'wilson-timeline-header-kpi-section',
  templateUrl: './timeline-header-kpi-section.component.html',
  styleUrls: ['./timeline-header-kpi-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimelineHeaderKPISectionComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly toaster = inject(NzMessageService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private productivityDataSourceService = inject(ProductivityDataSourceService);
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private router = inject(Router);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly shiftTimelineUnassignedRegionStateFacadeService = inject(
    ShiftTimelineUnassignedRegionStateFacadeService,
  );

  @Input() isKPICardOpen = true;

  showOvertimeAccountKpi$!: Observable<boolean>;
  showProductivityKpi$!: Observable<boolean>;
  showDeclinedShifts$!: Observable<boolean>;

  public visibleDateRange$ = this.store.select(
    ShiftTimelineDataState.visibleDateRange,
  );

  public isLoadingUnreleasedShifts$ = this.store.select(
    TimelineDataUtilityState.isLoadingUnreleasedShifts,
  );

  public hasSuccessfullyFetchedUsers$ = this.store.select(
    ShiftTimelineDataState.hasSuccessfullyFetchedUsers,
  );

  public isReleasingShift = false;

  public unassignedShiftsWithoutActivitiesCount$ = this.store
    .select(AuthState.userId)
    .pipe(
      filter((userId) => userId !== null),
      switchMap((userId: string) =>
        combineLatest([
          this.store.select(
            ShiftTimelinePreferredSettingsState.isCellView(userId),
          ),
          this.store.select(
            ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
          ),
        ]),
      ),
      switchMap(([isCellView, isUnassignedRegionExpanded]) => {
        if (!isUnassignedRegionExpanded) {
          return this.getCollapsedViewShiftsCount();
        } else {
          return isCellView
            ? this.getExpandedCellViewShiftsCount()
            : this.getStandardViewUnassignedShiftsCount();
        }
      }),
    );

  private subscription = new Subscription();
  private getExpandedCellViewShiftsCount(): Observable<number> {
    return this.shiftTimelineUnassignedRegionStateFacadeService.filteredUnassignedShiftsStacksWithoutActivities$.pipe(
      debounceTime(10),
      map((stacks) => {
        const visibleDateRange = this.store.selectSnapshot(
          ShiftTimelineDataState.visibleDateRange,
        );

        const flatShiftCount = stacks.reduce((total, stack) => {
          const isDayWithinInterval = isWithinInterval(
            new Date(stack.day),
            visibleDateRange,
          );
          if (isDayWithinInterval) {
            return total + Object.keys(stack.shifts).length;
          } else {
            return total;
          }
        }, 0);
        return flatShiftCount;
      }),
    );
  }

  private getCollapsedViewShiftsCount(): Observable<number> {
    return this.store
      .select(ShiftTimelineUnassignedRegionState.numberOfShiftPerDayRecords)
      .pipe(
        debounceTime(10),
        map((records: NumberOfShiftPerDayRecord) => {
          const visibleDateRange = this.store.selectSnapshot(
            ShiftTimelineDataState.visibleDateRange,
          );

          return Object.entries(records).reduce((sum, [key, value]) => {
            const isKeyWithinInterval = isWithinInterval(
              Number(key),
              visibleDateRange,
            );
            if (isKeyWithinInterval) {
              return sum + value;
            } else {
              return sum;
            }
          }, 0);
        }),
      );
  }

  private getStandardViewUnassignedShiftsCount(): Observable<number> {
    return this.shiftTimelineUnassignedRegionStateFacadeService.unassignedShiftsWithoutActivities$.pipe(
      debounceTime(10),
      map((shifts) => {
        const visibleDateRange = this.store.selectSnapshot(
          ShiftTimelineDataState.visibleDateRange,
        );
        const shiftsWithinVisibleDateRange = shifts.filter((value) =>
          isWithinInterval(
            new Date(getStartDatetime(value).date),
            visibleDateRange,
          ),
        );
        return shiftsWithinVisibleDateRange.length;
      }),
    );
  }

  public userOvertimeAverage$ =
    this.timelineCommonOperationsService.getUserOvertimeAverageStream();

  readonly isLoadingUsersProductivity$ =
    this.productivityDataSourceService.isLoadingUsersProductivityStream();

  public hasSuccessfullyFetchedUsersProductivity$ =
    this.productivityDataSourceService.hasFetchedUsersProductivitySuccessfully();

  public overallUserProductivity$ =
    this.productivityDataSourceService.averageUsersProductivityPercentage();

  public unreleasedShifts$ =
    this.shiftTimelineFilterStateService.filteredUsers$.pipe(
      switchMap((filteredUsers) => {
        const filteredUsersIds = filteredUsers.map(
          (filteredUser) => filteredUser.id as string,
        );
        return this.store.select(
          TimelineDataUtilityState.filteredUnreleasedShifts(filteredUsersIds),
        );
      }),
    );

  public declinedShifts$ =
    this.shiftTimelineFilterStateService.filteredUsers$.pipe(
      switchMap((filteredUsers) => {
        const filteredUsersIds = filteredUsers.map(
          (filteredUser) => filteredUser.id as string,
        );
        return this.store.select(
          TimelineDataUtilityState.filteredDeclinedShifts(filteredUsersIds),
        );
      }),
    );
  FeatureName = FeatureName;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  ngOnInit(): void {
    this.subscription.add(
      this.store
        .select(ShiftTimelineDataState.visibleDateRange)
        .pipe(
          debounceTime(10), //Debounce to avoid multiple calls to the server
          tap(() => {
            this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
          }),
        )
        .subscribe(),
    );

    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      this.showOvertimeAccountKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showOvertimeAccountKpi(userId),
      );
      this.showProductivityKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showProductivityKpi(userId),
      );
      this.showDeclinedShifts$ = combineLatest([
        this.store
          .select(AuthState.mobileSettings)
          .pipe(map((res) => res?.canDeclineShift)),
        this.featureFlagPurePipe.transform(
          'portal-mobile-settings-decline-shift',
        ),
      ]).pipe(
        map(([canDeclineShiftSetting, canDeclineShiftFeatureFlag]) =>
          canDeclineShiftSetting && canDeclineShiftFeatureFlag ? true : false,
        ),
      );
    }
  }

  async publishUnreleasedShifts(shifts: Shift[] | null): Promise<void> {
    if (shifts?.length) {
      this.isReleasingShift = true;
      try {
        const progressModalRef = this.nzModalService.create({
          nzContent: ShiftReleaseProgressModalComponent,
          nzData: {
            shiftsToRelease: shifts,
          } as ShiftReleaseProgressModalData,
          nzTitle: this.translate.instant('shifts.update.loading'),
          nzCancelText: this.translate.instant('general.cancel'),
          nzCentered: true,
          nzClosable: false,
          nzMaskClosable: false,
          nzKeyboard: false,
          nzOkDisabled: true,
        });
        progressModalRef.afterClose.pipe(take(1)).subscribe(() => {
          const updatedShiftIds =
            progressModalRef.componentInstance?.updatedShiftIds() || [];
          this.store.dispatch(new TimelinePublishShifts(updatedShiftIds));
          this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
        });
      } catch {
        this.toaster.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
      this.isReleasingShift = false;
    }
  }

  activateDeclineFilter(): void {
    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;
    if (
      params.has('confirmedDeclined') &&
      params.get('confirmedDeclined') === 'declined'
    ) {
      params.delete('confirmedDeclined');
    } else {
      params.set('confirmedDeclined', 'declined');
    }
    const newUrl = `${currentUrl.pathname}?${params.toString()}`;
    this.router.navigateByUrl(newUrl);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    // Flush to prevent memory leak
    this.unreleasedShifts$ = null as any;
    this.declinedShifts$ = null as any;
    this.showOvertimeAccountKpi$ = null as any;
    this.showProductivityKpi$ = null as any;
    this.showDeclinedShifts$ = null as any;
  }
}
